import React, { useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import CreatorFrame from '../../../../shared/CreatorFrame'
import DiaryDatePicker from '../../../../Diary/DiaryDatePicker'
import ShiftTimePicker from '../../../../Diary/ShiftTimePicker'
import { hapticTick, hapticSuccess } from '../../../../../_utils/haptics'
import { useScrollLock } from '../../../../../_utils/useScrollLock'
import { marketingApiService } from '../../../services/marketingApiService'
import { analyseSmsExpanded } from '../../sms-composer/smsUtils'
import PostStudio from '../../social/components/PostStudio'
import AutoGrow from '../../social/components/AutoGrow'
import { sparkleFrom } from '../../social/components/magic'
import { CheckIcon, CalendarIcon, ShuffleIcon, ChevronLeft } from '../../social/components/icons'
import { designName } from '../../social/data/postTypes'
import { deletePost } from '../../social/data/store'
import { CHANNEL_NAMES } from '../../plans/data/planner'
import { emailFor } from '../../plans/data/emailFor'
import { OPT_OUT } from '../../plans/data/sms'
import { approvePlan, blockedBecause, sendsMessages, creditsShort, needsBusinessDetails } from '../../plans/data/approve'
import { formatPence, creditsToPence } from '../../../../pricing/money'
import { hydrateBusinessDetails, loadBusinessDetails, isBusinessDetailsComplete } from '../../../../marketing/businessDetails'
import { marketingBackend } from '../../../services/marketingBackend'
import BusinessDetailsForm from '../../../../marketing/BusinessDetailsForm'
import { ChannelIcon, whenShort } from '../../plans/components/PlanCard'
import { SOULS } from '../../../../agent/frontdesk/soul'
import { saveCampaign, statusOf, duplicateCampaign } from '../data/campaignStore'
import { useServerStatuses } from '../data/serverStatus'
import { itemState } from '../data/itemState'
import { checkItems } from '../data/frequency'
import { LENGTHS, MAX_DAYS, newItem, respread, lengthOf, byDay } from '../data/build'
import { ROLES, sameness, tryAnother, relook } from '../data/story'
import { hasSeasonLook } from '../data/looks'
import { toRecord, campaignPath, LIST_PATH } from '../data/open'
import { CopySheet } from '../components/Sheets'
import { ItemThumb, ItemPreview, Storyboard, GridPreview, summaryOf } from './Previews'
import '../../plans/Plans.css'
import '../../social/Social.css'
import './Studio.css'

const TIMES = [{ h: 8, m: 0, label: '8am' }, { h: 10, m: 0, label: '10am' }, { h: 12, m: 30, label: '12:30pm' }, { h: 18, m: 0, label: '6pm' }, { h: 20, m: 30, label: '8:30pm' }]
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`
// The estimates still count in credits (1 = 2p); people see pounds.
const money = n => `£${(Number(n) || 0).toFixed(2)}`
const range = (a, b) => (moment(a).isSame(b, 'day') ? moment(a).format('ddd D MMM') : `${moment(a).format('ddd D MMM')} to ${moment(b).format('ddd D MMM')}`)
const dayName = d => moment(d).calendar(null, { sameDay: '[Today]', nextDay: '[Tomorrow]', nextWeek: 'dddd D MMM', sameElse: 'ddd D MMM', lastDay: '[Yesterday]', lastWeek: 'ddd D MMM' })
const timeOf = d => moment(d).format('h:mma').replace(':00', '')

const useIsPhone = () => {
  const q = '(max-width: 767px)'
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia && window.matchMedia(q).matches)
  useEffect(() => {
    if (!window.matchMedia) return undefined
    const m = window.matchMedia(q)
    const on = () => setPhone(m.matches)
    if (m.addEventListener) m.addEventListener('change', on); else m.addListener(on)
    return () => { if (m.removeEventListener) m.removeEventListener('change', on); else m.removeListener(on) }
  }, [])
  return phone
}

// An on/off switch (a button, so the app's label styles can't stretch it)
const Toggle = ({ on, onChange, label }) => (
  <button type="button" role="switch" aria-checked={on} aria-label={label} className={`mkp-switch${on ? ' is-on' : ''}`} onClick={() => { hapticTick(); onChange(!on) }}>
    <span aria-hidden />
  </button>
)

// When an item goes out: the diary's day picker and a few good times
const When = ({ at, onChange }) => {
  const [open, setOpen] = useState(false)
  const [note, setNote] = useState('')
  const ref = useRef(null)
  const m = moment(at)
  const set = (next) => {
    if (!next.isAfter(moment())) { setNote('That time has passed. Pick a later one.'); return }
    setNote('')
    onChange(next.toISOString())
  }
  return (
    <div className="mkp-when">
      <button type="button" ref={ref} className="mkp-when-day" onClick={() => setOpen(true)}><CalendarIcon size={15} />{m.format('ddd D MMM')}</button>
      <DiaryDatePicker open={open} selected={m.toDate()} anchorEl={ref.current} onChange={(d) => { setOpen(false); set(moment(d).hours(m.hours()).minutes(m.minutes())) }} onClose={() => setOpen(false)} />
      <div className="mkp-when-times">
        {TIMES.map(t => (
          <button key={t.label} type="button" className={`mkp-chip${m.hours() === t.h && m.minutes() === t.m ? ' is-on' : ''}`} onClick={() => { hapticTick(); set(moment(m).hours(t.h).minutes(t.m).seconds(0)) }}>{t.label}</button>
        ))}
        <span className="mkp-when-other">
          <ShiftTimePicker label="Send at" placeholder="Other" selected={TIMES.some(t => m.hours() === t.h && m.minutes() === t.m) ? null : m.toDate()} onChange={t => set(moment(m).hours(t.getHours()).minutes(t.getMinutes()).seconds(0))} />
        </span>
      </div>
      {note && <p className="mkp-note is-warn" role="alert">{note}</p>}
    </div>
  )
}

// A time moved outside the campaign's days stretches them to fit
const widen = (p, iso) => {
  const d = moment(iso).format('YYYY-MM-DD')
  if (!p.start) return { ...p, start: d, end: d }
  return { ...p, start: d < p.start ? d : p.start, end: d > p.end ? d : p.end }
}

const ADD_KINDS = [{ id: 'post', label: 'Post' }, { id: 'story', label: 'Story' }, { id: 'sms', label: 'Text' }, { id: 'email', label: 'Email' }]
const AddMenu = ({ onAdd }) => (
  <div className="mkp-add" role="group" aria-label="Add">
    {ADD_KINDS.map(k => (
      <button key={k.id} type="button" className="mkp-chip mkp-add-chip" onClick={() => onAdd(k.id)}><ChannelIcon channel={k.id} size={13} />{k.label}</button>
    ))}
  </div>
)

// Where an item is up to once the campaign is approved: what the server says
// happened to an email or text, not just whether its time has passed
const statusLabel = (it, made, server) => itemState(it, made, server).label

// The Campaign Studio: the days down the left, a big live preview on the
// right. A draft saves itself as it changes; approving schedules the emails
// and texts and plans the posts. An approved campaign opens here too, to see
// where it's up to, run it again, copy it or cancel what hasn't gone.
//   initial: the campaign (a plan to start from, or a saved one)
//   data: { others, segments, things, posts } from usePlans
// Cache only: the server save is awaited by the caller.
const saveBusinessDetailsCache = (salonId, details) => {
  try { localStorage.setItem(`beu-business-details-${salonId || 'default'}`, JSON.stringify(details)) } catch (e) { /* cache only */ }
}

const Studio = ({ initial, k, data, dispatch, managed, history, from, canCopy, group, active, onSavedId, onExit }) => {
  const [c, setC] = useState(() => ({ ...initial, title: initial.title || 'New campaign', items: initial.items || [] }))
  const [savedId, setSavedId] = useState(initial.id || null)
  const [savedAt, setSavedAt] = useState(initial.id ? initial.updatedAt : null)
  const [selected, setSelected] = useState(() => {
    const first = (initial.items || []).slice().sort((a, b) => new Date(a.at) - new Date(b.at))[0]
    return first ? first.id : null
  })
  const [tab, setTab] = useState('item')
  const [sheet, setSheet] = useState(false)
  const [datesOpen, setDatesOpen] = useState(false)
  const [startOpen, setStartOpen] = useState(false)
  const startRef = useRef(null)
  const [audienceOpen, setAudienceOpen] = useState(false)
  const [adding, setAdding] = useState(null)
  const [editing, setEditing] = useState(null)
  const [wordsOpen, setWordsOpen] = useState(false)
  const [whenOpen, setWhenOpen] = useState(false)
  const [estimates, setEstimates] = useState({})
  // The approve gate reads the business-details cache; on a device that has
  // never opened Config it's empty, and approving wrongly said "Add your
  // business details". Load them from the server, then re-check.
  const [, setBizLoaded] = useState(false)
  // The business details asked for on Approve (null when not asking)
  const [bizAsk, setBizAsk] = useState(null)
  const [bizSaving, setBizSaving] = useState(false)
  const [bizError, setBizError] = useState('')
  useEffect(() => {
    let alive = true
    hydrateBusinessDetails(localStorage.getItem('salonId')).then(() => { if (alive) setBizLoaded(true) }).catch(() => {})
    return () => { alive = false }
  }, [])
  const [phase, setPhase] = useState(initial.status === 'approved' || initial.status === 'cancelled' ? 'view' : 'edit') // edit | sending | done | partial | view
  const [steps, setSteps] = useState({})
  const [done, setDone] = useState(initial.created || {})
  // What really happened to the emails and texts it made
  const server = useServerStatuses([{ created: done }])
  const [copying, setCopying] = useState(false)
  const [confirmCancel, setConfirmCancel] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  // Why the last approve didn't all go (the server's reason, e.g. credits)
  const [failNote, setFailNote] = useState('')
  const [toast, setToast] = useState('')
  const doneRef = useRef(null)
  const changed = useRef(false)
  const phone = useIsPhone()
  const editable = phase === 'edit' || phase === 'partial'
  const viewOnly = phase === 'view'
  useScrollLock(!editing)

  // Pickers and sheets sit above the studio
  useEffect(() => {
    document.body.classList.add('mkp-open')
    return () => document.body.classList.remove('mkp-open')
  }, [])
  useEffect(() => {
    if (!toast) return undefined
    const t = setTimeout(() => setToast(''), 5000)
    return () => clearTimeout(t)
  }, [toast])

  const update = (fn) => { changed.current = true; setC(fn) }
  const setItem = (id, patch) => update(p => ({ ...p, items: p.items.map(it => (it.id === id ? { ...it, ...(typeof patch === 'function' ? patch(it) : patch) } : it)) }))

  // A draft keeps itself: saved a moment after each change. A new one gets its
  // own address the first time it's saved.
  const record = (extra = {}) => toRecord({ ...c, id: savedId || undefined, createdAt: c.createdAt }, extra)
  const saveNow = () => {
    const saved = saveCampaign(record({ status: 'draft' }))
    changed.current = false
    if (!savedId) { setSavedId(saved.id); if (onSavedId) onSavedId(saved.id) }
    setSavedAt(saved.updatedAt)
    return saved
  }
  useEffect(() => {
    if (!editable || !changed.current) return undefined
    const t = setTimeout(saveNow, 600)
    return () => clearTimeout(t)
  }, [c]) // eslint-disable-line react-hooks/exhaustive-deps

  const exit = () => {
    if (editable && changed.current) saveNow()
    onExit()
  }
  const saveAndExit = () => { hapticTick(); saveNow(); exit() }

  // Esc closes the phone preview first, then the studio
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || editing || copying) return
      if (sheet) { e.stopPropagation(); setSheet(false) }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [sheet, editing, copying])

  const sorted = useMemo(() => c.items.slice().sort((a, b) => new Date(a.at) - new Date(b.at)), [c.items])
  const onItems = sorted.filter(i => i.on)
  const audience = c.audience || { segment: 'all', label: 'All your clients' }
  const smsOn = onItems.find(i => i.channel === 'sms')
  const emailOn = onItems.find(i => i.channel === 'email')

  // Who it reaches and what a text costs, for this audience
  useEffect(() => {
    if (!editable) return undefined
    let alive = true
    const est = (type, extra = {}) => marketingApiService.estimateCost(dispatch, { type, audienceSegment: audience.segment || 'all', clientIds: [], ...extra }).catch(() => null)
    Promise.all([
      emailOn ? est('email') : null,
      // The text itself: the server prices it per segment from the body (a
      // `segments` field is ignored, which under-quoted long texts).
      smsOn ? est('sms', { message: smsOn.message }) : null,
    ]).then(([email, sms]) => { if (alive) setEstimates({ email, sms }) })
    return () => { alive = false }
  }, [audience.segment, !!emailOn, !!smsOn, smsOn && smsOn.message]) // eslint-disable-line react-hooks/exhaustive-deps

  const emails = useMemo(() => {
    const out = {}
    c.items.filter(i => i.channel === 'email' && i.email).forEach((i) => { out[i.id] = emailFor(i.email, k.ctx) })
    return out
  }, [c.items, k.ctx])

  const days = byDay(sorted)
  const length = lengthOf(c)
  const checks = useMemo(() => checkItems(c.items, data.others || []), [c.items, data.others])
  const same = useMemo(() => sameness(c.items), [c.items])
  const hardStop = Object.values(checks).find(x => x && x.block)
  // Credits for every email and text in it, together (they share a wallet)
  const credit = creditsShort(estimates, {
    smsCount: onItems.filter(i => i.channel === 'sms').length,
    emailCount: onItems.filter(i => i.channel === 'email').length,
  })
  const creditBlock = credit && credit.short > 0
    ? `Your message balance is too low: this needs about ${formatPence(creditsToPence(Math.ceil(credit.needed)))} and you have ${formatPence(creditsToPence(Math.floor(credit.have)))}. Top up on the Campaigns page, or send it to fewer clients.`
    : ''
  const blocked = editable ? (blockedBecause(c, { managed, tz: k.salon && k.salon.timezone, askForBusinessDetails: true }) || (hardStop ? hardStop.block : '') || creditBlock) : ''
  // Emails and texts must name the sender (UK law). Missing details don't grey
  // out Approve: approving asks for them, saves them, then carries on.
  const bizMissing = editable && !managed && needsBusinessDetails(c)
  const sel = c.items.find(i => i.id === selected) || null
  const links = k.previewLinks || {}
  // "Halloween colours", or "Season colours" for "the new year"
  const seasonName = c.event && !/^the /i.test(c.event) ? `${c.event.charAt(0).toUpperCase()}${c.event.slice(1)}` : 'Season'
  const lookOpts = [
    hasSeasonLook(c.moment) && { id: 'season', label: `${seasonName} colours` },
    k.brandColour && { id: 'brand', label: 'Your brand' },
    { id: 'own', label: 'Each design’s own' },
  ].filter(Boolean)
  const look = c.look || (hasSeasonLook(c.moment) ? 'season' : k.brandColour ? 'brand' : 'own')
  const tone = data.things && data.things.tone
  const soulName = tone ? (SOULS.find(s => s.id === tone) || {}).name : null

  const pick = (id) => {
    hapticTick()
    setSelected(id)
    setTab('item')
    setWordsOpen(false)
    setWhenOpen(false)
    if (phone) setSheet('item')
  }
  const addOn = (channel, day) => {
    hapticTick()
    let it = newItem(channel, day || c.start || moment().add(1, 'day'), { trade: k.trade, brandColour: k.brandColour, family: k.family, salonName: k.salonName, name: c.kind === 'yours' ? '' : (c.event || c.title) })
    if (it.post) it = relook([it], look, { momentId: c.moment, brandColour: k.brandColour })[0]
    update(p => ({ ...p, items: [...p.items, it] }))
    setAdding(null)
    setSelected(it.id)
    setTab('item')
    setWordsOpen(it.channel === 'sms' || it.channel === 'email')
    if (phone) setSheet('item')
  }
  const removeItem = (id) => {
    hapticTick()
    const next = sorted.filter(i => i.id !== id)
    update(p => ({ ...p, items: p.items.filter(i => i.id !== id) }))
    setSelected(next.length ? next[0].id : null)
    if (phone) setSheet(false)
  }
  const another = () => {
    if (!sel || !sel.post) return
    hapticTick()
    setItem(sel.id, tryAnother(sel, c.items, { look, momentId: c.moment, brandColour: k.brandColour }))
  }
  const setLook = (id) => {
    if (id === look) return
    hapticTick()
    update(p => ({ ...p, look: id, items: relook(p.items, id, { momentId: p.moment, brandColour: k.brandColour }) }))
  }
  const setDates = (start, n) => { hapticTick(); update(p => respread(p, start, n)) }

  // ─── Approving ───
  const count = e => (e && Number(e.recipientCount)) || audience.count || null
  const smsCost = estimates.sms && (estimates.sms.cost !== undefined || estimates.sms.totalCost !== undefined) ? Number(estimates.sms.cost ?? estimates.sms.totalCost) : null
  const postCount = onItems.filter(i => i.channel === 'post').length
  const storyCount = onItems.filter(i => i.channel === 'story').length
  const emailCount = onItems.filter(i => i.channel === 'email').length
  const smsCount = onItems.filter(i => i.channel === 'sms').length
  const summary = []
  if (emailCount) summary.push(`${plural(emailCount, 'email', 'emails')} to ${count(estimates.email) ? `about ${plural(count(estimates.email), 'client', 'clients')}` : 'your clients'}`)
  if (smsCount) summary.push(`${plural(smsCount, 'text', 'texts')} to ${count(estimates.sms) ? `about ${plural(count(estimates.sms), 'client', 'clients')}` : 'your clients'}${smsCost != null ? ` (up to ${money(smsCost * smsCount)} from your balance)` : ''}`)
  if (postCount || storyCount) summary.push(`${[postCount && plural(postCount, 'post', 'posts'), storyCount && plural(storyCount, 'Story', 'Stories')].filter(Boolean).join(' and ')} with a reminder when it’s time to share`)

  // On a phone, the same in a line
  const short = [
    emailCount && plural(emailCount, 'email', 'emails'),
    smsCount && `${plural(smsCount, 'text', 'texts')}${smsCost != null ? ` (up to ${money(smsCost * smsCount)})` : ''}`,
    postCount && plural(postCount, 'post', 'posts'),
    storyCount && plural(storyCount, 'Story', 'Stories'),
  ].filter(Boolean).join(', ')

  const approve = async (opts) => {
    if (blocked || phase === 'sending') return
    if (bizMissing && !(opts && opts.bizSaved)) {
      hapticTick()
      setBizAsk(loadBusinessDetails(localStorage.getItem('salonId')) || { legalForm: '', registeredName: '', companyNumber: '', placeOfRegistration: 'England and Wales', registeredOffice: '', tradingName: '' })
      return
    }
    hapticTick()
    setPhase('sending')
    setFailNote('')
    const draft = saveCampaign(record({ status: 'draft' }))
    changed.current = false
    if (!savedId) { setSavedId(draft.id); if (onSavedId) onSavedId(draft.id) }
    const results = await approvePlan({ ...c, key: c.key || null }, {
      dispatch,
      ctx: k.ctx,
      done,
      campaignId: draft.id,
      onStep: (id, s) => setSteps(x => ({ ...x, [id]: s })),
    })
    const made = { ...done }
    results.forEach((r) => { if (r.ok) made[r.id] = r.created })
    setDone(made)
    const all = results.every(r => r.ok)
    const saved = saveCampaign(record({ id: draft.id, status: all ? 'approved' : 'draft', created: made, ...(all ? { approvedAt: new Date().toISOString() } : {}) }))
    setSavedAt(saved.updatedAt)
    setC(p => ({ ...p, status: saved.status, created: made, approvedAt: saved.approvedAt, createdAt: saved.createdAt }))
    if (all) {
      setPhase('done')
      hapticSuccess()
      setTimeout(() => sparkleFrom(doneRef.current, 14), 60)
    } else {
      const failed = results.filter(r => !r.ok)
      setFailNote(`${plural(failed.length, 'thing', 'things')} didn’t go: ${failed[0].error}`)
      setPhase('partial')
    }
  }

  // ─── An approved campaign ───
  const now = moment()
  const unsent = viewOnly ? c.items.filter(i => i.on && moment(i.at).isAfter(now) && done[i.id]) : []
  const runAgain = () => {
    hapticTick()
    const d = saveCampaign(duplicateCampaign(toRecord(c)))
    history.replace(campaignPath(d.id), { from: from || null })
  }
  const cancelRest = async () => {
    setBusy(true); setError('')
    const failed = []
    // eslint-disable-next-line no-restricted-syntax
    for (const it of unsent) {
      const made = done[it.id]
      try {
        if (made.channel === 'post' || made.channel === 'story') deletePost(made.id)
        // eslint-disable-next-line no-await-in-loop
        else await marketingApiService.deleteCampaign(dispatch, made.id)
      } catch (e) { failed.push(it.id) }
    }
    const on = c.items.filter(i => i.on)
    if (failed.length) {
      setError(`${plural(failed.length, 'thing', 'things')} couldn’t be cancelled. Try again, or cancel ${failed.length === 1 ? 'it' : 'them'} from Emails or Texts.`)
      const keep = {}
      Object.keys(done).forEach((id) => { if (failed.includes(id) || !unsent.find(u => u.id === id)) keep[id] = done[id] })
      setDone(keep)
      saveCampaign(toRecord({ ...c, created: keep }))
      setBusy(false)
      return
    }
    hapticSuccess()
    const items = c.items.map(i => (unsent.find(u => u.id === i.id) ? { ...i, on: false, cancelled: true } : i))
    const saved = saveCampaign(toRecord({ ...c, items, created: done, status: on.length === unsent.length ? 'cancelled' : 'approved' }))
    setC(p => ({ ...p, items, status: saved.status }))
    setBusy(false)
    setConfirmCancel(false)
    setToast(`${plural(unsent.length, 'thing', 'things')} cancelled. What had already gone stays gone.`)
  }

  if (editing) {
    const item = c.items.find(i => i.id === editing)
    return (
      <PostStudio
        post={item.post}
        kit={k.kit}
        trade={k.trade}
        salonName={k.salonName}
        town={k.town}
        salon={k.salon}
        onClose={(saved) => { if (saved) setItem(editing, { post: { ...saved, status: 'draft' } }); setEditing(null) }}
      />
    )
  }

  const st = statusOf(toRecord(c), new Date(), server)
  const settled = viewOnly || phase === 'done'
  const savedLabel = phase === 'sending' ? 'Scheduling…' : settled ? st.label : savedAt ? `Draft · saved ${moment(savedAt).fromNow()}` : 'Draft'
  const primary = viewOnly
    ? { label: 'Run it again', onClick: runAgain }
    : phase === 'done' ? null
      : { label: phase === 'sending' ? 'Scheduling…' : phase === 'partial' ? 'Try again' : sendsMessages(c) ? 'Approve and schedule' : 'Approve', onClick: approve, disabled: !!blocked || phase === 'sending' }

  // The item panel: what it is, when it goes, whether it repeats anything,
  // and what can be done to it
  const position = sel ? onItems.findIndex(i => i.id === sel.id) + 1 : 0
  const repeat = sel && same[sel.id]
  const repeatOf = repeat && c.items.find(i => i.id === repeat.with)
  const chk = sel && checks[sel.id]
  const made = sel && done[sel.id]
  const panel = sel && (
    <div className="cst-item">
      <span className="cst-item-kicker">
        <span className="cst-role">{ROLES[sel.role] || 'Yours'}</span>
        {sel.on && position ? <span>{position} of {onItems.length}</span> : <span>Left out</span>}
      </span>
      <h3 className="cst-item-title">{summaryOf(sel, { salonName: k.salonName, links }) || CHANNEL_NAMES[sel.channel]}</h3>
      <dl className="cst-facts">
        {sel.post && (<><dt>Design</dt><dd>{designName(sel.post.type, sel.post.family, sel.post.layout) || 'Your design'}</dd></>)}
        <dt>Words</dt>
        <dd>{sel.added ? 'Yours' : soulName ? `Bailey’s, in your soul (${soulName})` : 'Bailey’s'}</dd>
        <dt>{sel.post ? 'Goes' : 'Sends'}</dt>
        <dd>
          {editable && sel.on
            ? <button type="button" className="cst-link-btn" onClick={() => { hapticTick(); setWhenOpen(o => !o) }} aria-expanded={whenOpen}>{moment(sel.at).format('ddd D MMM')}, {timeOf(sel.at)}</button>
            : <>{moment(sel.at).format('ddd D MMM')}, {timeOf(sel.at)}</>}
          {sel.post ? '. Bailey reminds you to share it.' : ''}
        </dd>
        {viewOnly && (<><dt>Now</dt><dd>{statusLabel(sel, made, server)}</dd></>)}
      </dl>
      {whenOpen && editable && sel.on && <When at={sel.at} onChange={(iso) => { setItem(sel.id, { at: iso }); update(p => widen(p, iso)) }} />}
      {sel.on && (repeat
        ? <p className="cst-variety is-warn">{repeat.what === 'words' ? 'Says the same as' : 'The same design as'} the {CHANNEL_NAMES[repeatOf.channel].toLowerCase()} on {moment(repeatOf.at).format('ddd D MMM')}.{sel.post && editable ? ' Try another.' : ''}</p>
        : <p className="cst-variety"><CheckIcon size={13} />Different from everything else in this campaign</p>)}
      {sel.on && chk && (chk.block || chk.warn) && <p className={`mkp-note ${chk.block ? 'is-stop' : 'is-warn'}`}>{chk.block || chk.warn}</p>}
      {sel.check && <p className="mkp-note is-warn">These free times came from {c.copiedFrom && c.copiedFrom.salonName ? c.copiedFrom.salonName : 'another location'}’s diary. Check them before approving.</p>}
      {wordsOpen && editable && sel.channel === 'sms' && (
        <div className="cst-fields">
          <AutoGrow minRows={3} className="mkp-input" value={sel.message} onChange={v => setItem(sel.id, { message: v, added: true })} aria-label="Text message" />
          {(() => {
            const a = analyseSmsExpanded(sel.message, k.salonName)
            return <span className={`mkp-note${a.segments > 1 ? ' is-warn' : ''}`}>{a.segments === 1 ? 'Fits in one text.' : `About ${a.segments} texts long, so it costs ${a.segments} times as much.`}{!/reply stop/i.test(sel.message) ? ' UK rules need “Reply STOP to opt out” in every marketing text.' : ''}</span>
          })()}
          {!/reply stop/i.test(sel.message) && <button type="button" className="cst-link-btn" onClick={() => setItem(sel.id, { message: `${sel.message.trimEnd()}\n${OPT_OUT}` })}>Add the opt-out line</button>}
        </div>
      )}
      {wordsOpen && editable && sel.channel === 'email' && (
        <div className="cst-fields">
          <label><span className="mkp-label">Subject</span><AutoGrow singleLine className="mkp-input" value={sel.email.subject} onChange={v => setItem(sel.id, x => ({ added: true, email: { ...x.email, subject: v } }))} /></label>
          <label><span className="mkp-label">Headline</span><AutoGrow className="mkp-input" value={sel.email.hero.headline} onChange={v => setItem(sel.id, x => ({ added: true, email: { ...x.email, hero: { ...x.email.hero, headline: v } } }))} /></label>
          <label><span className="mkp-label">Message</span><AutoGrow minRows={3} className="mkp-input" value={sel.email.hero.body} onChange={v => setItem(sel.id, x => ({ added: true, email: { ...x.email, hero: { ...x.email.hero, body: v } } }))} /></label>
          <label><span className="mkp-label">Button</span><AutoGrow singleLine className="mkp-input" value={sel.email.hero.button} onChange={v => setItem(sel.id, x => ({ added: true, email: { ...x.email, hero: { ...x.email.hero, button: v } } }))} /></label>
        </div>
      )}
      {editable && (
        <div className="cst-item-actions">
          {sel.post && sel.on && <button type="button" className="cst-btn" onClick={() => { hapticTick(); setEditing(sel.id) }}>Edit the design</button>}
          {sel.post && sel.on && <button type="button" className="cst-btn" onClick={another}><ShuffleIcon size={15} />Try another</button>}
          {!sel.post && sel.on && <button type="button" className="cst-btn" onClick={() => { hapticTick(); setWordsOpen(o => !o) }} aria-expanded={wordsOpen}>{wordsOpen ? 'Done' : 'Edit the words'}</button>}
          <span className="cst-item-switch">
            <span>{sel.on ? 'Included' : 'Left out'}</span>
            <Toggle on={sel.on} onChange={v => setItem(sel.id, { on: v })} label={`Include the ${CHANNEL_NAMES[sel.channel].toLowerCase()}`} />
          </span>
          <button type="button" className="cst-link-btn is-quiet" onClick={() => removeItem(sel.id)}>Take it out</button>
        </div>
      )}
      {viewOnly && made && (sel.channel === 'email' || sel.channel === 'sms') && (
        <div className="cst-item-actions">
          <button type="button" className="cst-btn" onClick={() => history.push(LIST_PATH, { openCampaignId: made.id })}>{moment(sel.at).isSameOrBefore(now) ? 'See the results' : `Open in ${sel.channel === 'email' ? 'Emails' : 'Texts'}`}</button>
        </div>
      )}
    </div>
  )

  const preview = sel
    ? <ItemPreview item={sel} kit={k.kit} salonName={k.salonName} town={k.town} built={emails[sel.id]} links={links} />
    : <p className="cst-muted cst-empty-line">{c.items.length ? 'Pick something on the left to see it here.' : 'Add a post, Story, text or email to see it here.'}</p>

  const board = <Storyboard items={c.items} kit={k.kit} emails={emails} salonName={k.salonName} links={links} selected={selected} onPick={(id) => { setSelected(id); setTab('item'); if (phone) setSheet('item') }} />
  const grid = <GridPreview items={c.items} kit={k.kit} posts={data.posts} campaignId={savedId} selected={selected} onPick={(id) => { setSelected(id); setTab('item'); if (phone) setSheet('item') }} />

  const TABS = [{ id: 'item', label: 'This item' }, { id: 'board', label: 'Storyboard' }, { id: 'grid', label: 'Your grid' }]

  return (
    <CreatorFrame
      name={c.title}
      namePlaceholder="Name this campaign"
      onNameChange={editable ? (v => update(p => ({ ...p, title: v }))) : undefined}
      onClose={exit}
      headerExtra={<span className={`cst-status is-${settled ? st.id : 'draft'}`}>{savedLabel}</span>}
      onSave={editable ? saveAndExit : viewOnly && canCopy ? () => { hapticTick(); setCopying(true) } : undefined}
      saveLabel={editable ? 'Save as draft' : 'Copy to other locations'}
      saveVariant="secondary"
      primaryAction={phone ? null : primary}
    >
      <div className={`cst${phone ? ' is-phone' : ''}`}>
        {phase === 'done' ? (
          <div className="cst-done" ref={doneRef}>
            <span className="cst-done-tick" aria-hidden><CheckIcon size={30} /></span>
            <h2>All set</h2>
            <p>
              {emailCount || smsCount ? 'Your campaign is scheduled. ' : ''}
              {postCount || storyCount ? 'Bailey will remind you on the day each post and Story is ready to share, here and in the diary.' : ''}
            </p>
            <ul className="cst-done-list">
              {onItems.map(it => (
                <li key={it.id}><span className={`mkp-step-icon is-${it.channel}`}><ChannelIcon channel={it.channel} size={13} /></span><b>{CHANNEL_NAMES[it.channel]}</b><span>{whenShort(it.at)}</span></li>
              ))}
            </ul>
            <div className="cst-done-actions">
              <button type="button" className="cst-btn" onClick={() => { hapticTick(); setPhase('view') }}>See the campaign</button>
              <button type="button" className="cst-primary" onClick={exit}>Done</button>
            </div>
          </div>
        ) : (
          <>
            <div className="cst-bar">
              {phone && editable && (
                <input className="cst-name" value={c.title} maxLength={80} placeholder="Name this campaign" aria-label="Campaign name" onChange={(e) => { const v = e.target.value; update(p => ({ ...p, title: v })) }} />
              )}
              {phone && !editable && <h1 className="cst-name is-static">{c.title}</h1>}
              <div className="cst-chips">
                <button type="button" className={`cst-chip${datesOpen ? ' is-open' : ''}`} disabled={!editable} onClick={() => { hapticTick(); setDatesOpen(o => !o); setAudienceOpen(false) }} aria-expanded={datesOpen}>
                  <CalendarIcon size={14} />{c.start ? range(c.start, c.end) : 'Pick the dates'}<small>{plural(length, 'day', 'days')}</small>
                </button>
                {(sendsMessages({ items: c.items.map(i => ({ ...i, on: true })) }) || c.kind === 'yours') && (
                  <button type="button" className={`cst-chip${audienceOpen ? ' is-open' : ''}`} disabled={!editable || (data.segments || []).length < 2} onClick={() => { hapticTick(); setAudienceOpen(o => !o); setDatesOpen(false) }} aria-expanded={audienceOpen}>
                    {audience.label}{count(estimates.email || estimates.sms) ? <small>{count(estimates.email || estimates.sms)}</small> : null}
                  </button>
                )}
                {editable && lookOpts.length > 1 && (
                  <div className="cst-seg is-small" role="radiogroup" aria-label="Colours">
                    {lookOpts.map(o => <button key={o.id} type="button" role="radio" aria-checked={look === o.id} className={`cst-seg-btn${look === o.id ? ' is-on' : ''}`} onClick={() => setLook(o.id)}>{o.label}</button>)}
                  </div>
                )}
                {c.copiedFrom && <span className="cst-chip is-static">Copied from {c.copiedFrom.salonName || 'another location'}</span>}
              </div>
              {datesOpen && editable && (
                <div className="cst-drop">
                  <div className="mkp-when">
                    <button type="button" ref={startRef} className="mkp-when-day" onClick={() => setStartOpen(true)}><CalendarIcon size={15} />Starts {moment(c.start).format('ddd D MMM')}</button>
                    <DiaryDatePicker open={startOpen} selected={moment(c.start).toDate()} anchorEl={startRef.current} onChange={(d) => { setStartOpen(false); if (moment(d).isBefore(moment(), 'day')) return; setDates(d, length) }} onClose={() => setStartOpen(false)} />
                    <div className="mkp-when-times" role="radiogroup" aria-label="How long it runs">
                      {LENGTHS.map(l => <button key={l.days} type="button" role="radio" aria-checked={length === l.days} className={`mkp-chip${length === l.days ? ' is-on' : ''}`} onClick={() => setDates(c.start, l.days)}>{l.label}</button>)}
                    </div>
                    <p className="mkp-note">Everything keeps its place in the campaign when the dates change. Up to {MAX_DAYS / 7} weeks.</p>
                  </div>
                </div>
              )}
              {audienceOpen && editable && (
                <div className="cst-drop mkp-segments" role="radiogroup" aria-label="Who it’s for">
                  {(data.segments || []).filter(s => s && s.id !== 'custom').map(s => (
                    <button key={s.id} type="button" role="radio" aria-checked={audience.segment === s.id} className={`mkp-segment${audience.segment === s.id ? ' is-on' : ''}`} onClick={() => { hapticTick(); update(p => ({ ...p, audience: { segment: s.id, label: s.name, count: Number(s.count) || null } })) }}>
                      <b>{s.name}</b>{s.count != null && <span>{plural(Number(s.count) || 0, 'client', 'clients')}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="cst-main">
              <section className="cst-days" aria-label="The days">
                {(c.why || (c.notes && c.notes.length > 0)) && (
                  <div className="cst-why">
                    {c.why && <p>{c.why}</p>}
                    {(c.notes || []).map(n => <p key={n} className="cst-note">{n}</p>)}
                  </div>
                )}
                {phone && c.items.length > 0 && (
                  <div className="cst-phone-views">
                    <button type="button" className="cst-btn" onClick={() => { hapticTick(); setSheet('board') }}>Storyboard</button>
                    <button type="button" className="cst-btn" onClick={() => { hapticTick(); setSheet('grid') }}>Your grid</button>
                  </div>
                )}
                {!sorted.length && (
                  <div className="cst-empty">
                    <b>Nothing in it yet</b>
                    <span>Add posts, Stories, texts and emails. Each one gets its own day and time.</span>
                  </div>
                )}
                {days.map(({ day, items }, di) => (
                  <div key={day} className="cst-day">
                    {length > 7 && (di === 0 || moment(day).isoWeek() !== moment(days[di - 1].day).isoWeek()) && (
                      <span className="cst-week">Week {Math.floor(moment(day).startOf('isoWeek').diff(moment(c.start || day).startOf('isoWeek'), 'weeks')) + 1}</span>
                    )}
                    <div className="cst-day-head">
                      <b>{dayName(day)}</b>
                      {editable && <button type="button" className="cst-day-add" onClick={() => { hapticTick(); setAdding(a => (a === day ? null : day)) }} aria-expanded={adding === day}>{adding === day ? 'Close' : '+ Add'}</button>}
                    </div>
                    {adding === day && <AddMenu onAdd={ch => addOn(ch, day)} />}
                    <ol className="cst-rows">
                      {items.map((it) => {
                        const x = checks[it.id]
                        const s = same[it.id]
                        const step = steps[it.id]
                        const flag = !it.on ? (it.cancelled ? 'Cancelled' : 'Left out')
                          : x && x.block ? 'Texts go out 9am to 8pm'
                            : s ? (s.what === 'words' ? 'Same words as another' : 'Same design as another')
                              : x && x.warn ? (it.channel === 'sms' ? 'Close to another text' : 'Close to another email')
                                : it.check ? 'Check the free times' : ''
                        return (
                          <li key={it.id}>
                            <button type="button" className={`cst-row${selected === it.id ? ' is-on' : ''}${it.on && !it.cancelled ? '' : ' is-off'}`} onClick={() => pick(it.id)} aria-current={selected === it.id ? 'true' : undefined}>
                              <ItemThumb item={it} kit={k.kit} />
                              <span className="cst-row-text">
                                <span className="cst-row-top">
                                  <b>{CHANNEL_NAMES[it.channel]}</b>
                                  <span>{timeOf(it.at)}</span>
                                  {it.role && ROLES[it.role] && <span className="cst-role is-small">{ROLES[it.role]}</span>}
                                </span>
                                <span className="cst-row-sum">{summaryOf(it, { salonName: k.salonName, links })}</span>
                                {viewOnly && <span className="cst-row-state">{statusLabel(it, done[it.id], server)}</span>}
                                {!viewOnly && flag && <span className={`cst-row-flag${x && x.block ? ' is-stop' : ''}`}>{flag}</span>}
                              </span>
                              {step === 'working' && <span className="mkp-spin" aria-label="Scheduling" />}
                              {step === 'done' && <span className="cst-row-ok" aria-label="Done"><CheckIcon size={13} /></span>}
                              {step === 'failed' && <span className="mkp-failed">Didn’t go</span>}
                            </button>
                          </li>
                        )
                      })}
                    </ol>
                  </div>
                ))}
                {editable && (
                  <div className="cst-add-bar">
                    <span className="mkp-label">Add a post, Story, text or email</span>
                    <AddMenu onAdd={(ch) => {
                      // The first day without one of these, else the first day
                      const free = []
                      for (let d = 0; d < length; d += 1) free.push(moment(c.start).add(d, 'days').format('YYYY-MM-DD'))
                      const day = free.find(d => !c.items.some(i => i.channel === ch && moment(i.at).format('YYYY-MM-DD') === d)) || c.start
                      addOn(ch, day)
                    }} />
                  </div>
                )}
              </section>

              {!phone && (
                <section className="cst-pane" aria-label="Preview">
                  <div className="cst-tabs" role="tablist" aria-label="Preview">
                    {TABS.map(t => <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} className={`cst-tab${tab === t.id ? ' is-on' : ''}`} onClick={() => { hapticTick(); setTab(t.id) }}>{t.label}</button>)}
                  </div>
                  {tab === 'item' && (
                    <div className="cst-pane-item" key={selected || 'none'}>
                      <div className="cst-preview">{preview}</div>
                      {panel}
                    </div>
                  )}
                  {tab === 'board' && <div className="cst-pane-wide">{board}</div>}
                  {tab === 'grid' && <div className="cst-pane-wide">{grid}</div>}
                </section>
              )}
            </div>

            <div className="cst-foot">
              {phase === 'partial' && <p className="mkp-note is-warn" role="alert">Some of this didn’t go. Nothing was sent twice. Try again, or leave out what didn’t go.</p>}
              {viewOnly ? (
                confirmCancel ? (
                  <div className="cst-confirm">
                    <p><b>Cancel what hasn’t gone?</b> {plural(unsent.length, 'thing', 'things')} will be taken off: scheduled emails and texts are deleted and planned posts removed. What’s already gone stays gone.</p>
                    {error && <p className="mkp-note is-stop" role="alert">{error}</p>}
                    <div className="cst-foot-actions">
                      <button type="button" className="cst-btn" disabled={busy} onClick={() => setConfirmCancel(false)}>Keep it</button>
                      <button type="button" className="cst-primary is-danger" disabled={busy} onClick={cancelRest}>{busy ? 'Cancelling…' : 'Cancel them'}</button>
                    </div>
                  </div>
                ) : (
                  <div className="cst-foot-row">
                    <p className="cst-summary">{range(c.start, c.end)}{c.audience && c.audience.label ? `, to ${String(c.audience.label).toLowerCase()}` : ''}. {st.id === 'attention' ? `${st.problems === 1 ? 'One thing' : `${st.problems} things`} didn’t go. Tap ${st.problems === 1 ? 'it' : 'each'} to see why.` : st.id === 'running' ? `${st.sent} of ${st.total} gone so far.` : st.id === 'sent' ? 'Everything has gone.' : st.id === 'cancelled' ? 'Cancelled.' : 'Nothing has gone yet.'}</p>
                    <div className="cst-foot-actions">
                      {unsent.length > 0 && <button type="button" className="cst-btn is-quiet" onClick={() => { hapticTick(); setConfirmCancel(true) }}>Cancel what hasn’t gone</button>}
                      {phone && canCopy && <button type="button" className="cst-btn" onClick={() => setCopying(true)}>Copy</button>}
                      {phone && <button type="button" className="cst-primary" onClick={runAgain}>Run it again</button>}
                    </div>
                  </div>
                )
              ) : (
                <div className="cst-foot-row">
                  <p className={`cst-summary${blocked || (phase === 'partial' && failNote) ? ' is-warn' : ''}`} role={phase === 'partial' && failNote ? 'alert' : undefined}>{blocked || (phase === 'partial' && failNote) || (summary.length ? `${phone ? short : `Approving schedules ${summary.join(', ').replace(/, ([^,]*)$/, ' and $1')}.`}${bizMissing ? ` You’ll add your business details first.` : ''}` : '')}</p>
                  {phone && (
                    <div className="cst-foot-actions">
                      <button type="button" className="cst-btn" onClick={saveAndExit} disabled={phase === 'sending'}>Save as draft</button>
                      <button type="button" className="cst-primary" onClick={approve} disabled={!!blocked || phase === 'sending'}>{primary && primary.label}</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {phone && sheet && (
          <div className="cst-sheet" role="dialog" aria-modal="true" aria-label={sheet === 'board' ? 'Storyboard' : sheet === 'grid' ? 'Your grid' : 'Preview'}>
            <div className="cst-sheet-head">
              <button type="button" className="cf-iconbtn" onClick={() => setSheet(false)} aria-label="Back"><ChevronLeft size={18} /></button>
              <b>{sheet === 'board' ? 'Storyboard' : sheet === 'grid' ? 'Your grid' : sel ? `${CHANNEL_NAMES[sel.channel]}, ${whenShort(sel.at)}` : 'Preview'}</b>
            </div>
            <div className="cst-sheet-body">
              {sheet === 'item' && <>{<div className="cst-preview">{preview}</div>}{panel}</>}
              {sheet === 'board' && board}
              {sheet === 'grid' && grid}
            </div>
            {sheet === 'item' && sel && sel.post && editable && sel.on && (
              <div className="cst-sheet-foot">
                <button type="button" className="cst-btn" onClick={another}><ShuffleIcon size={15} />Try another</button>
                <button type="button" className="cst-primary" onClick={() => { hapticTick(); setEditing(sel.id) }}>Edit the design</button>
              </div>
            )}
          </div>
        )}

        {copying && (
          <CopySheet
            campaign={toRecord(c)}
            sites={group}
            active={active}
            onDone={(chosen) => { setCopying(false); setToast(`A draft is waiting at ${chosen.map(s => s.salonName).join(', ').replace(/, ([^,]*)$/, ' and $1')}. Switch to each location to check and approve it.`) }}
            onClose={() => setCopying(false)}
          />
        )}
        {bizAsk && (
          <div className="cst-biz-backdrop" onClick={() => { if (!bizSaving) { setBizAsk(null); setBizError('') } }}>
            <div className="cst-biz" role="dialog" aria-modal="true" aria-labelledby="cst-biz-title" onClick={(e) => e.stopPropagation()}>
              <h2 id="cst-biz-title">One thing before it goes</h2>
              <p>UK law says marketing emails and texts must say who they’re from. Tell us how your business is set up and we’ll add it to every one. You only do this once.</p>
              <BusinessDetailsForm
                value={bizAsk}
                onChange={(field, value) => setBizAsk(prev => ({ ...prev, [field]: value }))}
                prefillAddress={k.salon && (k.salon.formattedAddress || k.salon.address)}
              />
              {bizError && <p className="cst-biz-error" role="alert">{bizError}</p>}
              <div className="cst-biz-actions">
                <button type="button" className="cst-btn" onClick={() => { setBizAsk(null); setBizError('') }} disabled={bizSaving}>Not now</button>
                <button
                  type="button"
                  className="cst-primary"
                  disabled={!isBusinessDetailsComplete(bizAsk) || bizSaving}
                  onClick={async () => {
                    // The send engine reads the server copy, so don't schedule
                    // until the server has it.
                    setBizSaving(true)
                    setBizError('')
                    try {
                      await marketingBackend.businessDetailsSave(bizAsk)
                    } catch (e) {
                      setBizSaving(false)
                      setBizError('We couldn’t save your details. Check your connection and try again.')
                      return
                    }
                    saveBusinessDetailsCache(localStorage.getItem('salonId'), bizAsk)
                    setBizSaving(false)
                    setBizAsk(null)
                    approve({ bizSaved: true })
                  }}
                >
                  {bizSaving ? 'Saving…' : 'Save and schedule'}
                </button>
              </div>
            </div>
          </div>
        )}
        {toast && <div className="sx-toast" role="status">{toast}</div>}
      </div>
    </CreatorFrame>
  )
}

export default Studio
